import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { Typography, Breadcrumbs, Box, Link, Grid, Card, CardContent, Chip, CircularProgress } from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import API from '@utils/axios';
import Layout from '@components/Layout';
import CustomerTabs from '@components/customer/CustomerTabs';

function CustomerIntegrationsPage() {
    const { t } = useTranslation();
    const { customerId } = useParams();
    const [customer, setCustomer] = useState(null);
    const [integrations, setIntegrations] = useState([]);
    const [integrationsLoaded, setIntegrationsLoaded] = useState(false);

    const fetchCustomer = useCallback(async () => {
        const response = await API.get(`/customers/${customerId}`);
        setCustomer(response.data);
    }, [customerId]);

    const fetchIntegrations = useCallback(async () => {
        try {
            const response = await API.get(`/customers/${customerId}/services-urls`);
            setIntegrations(response.data);
        } catch (error) {
            console.error('Error fetching integrations:', error);
        } finally {
            setIntegrationsLoaded(true);
        }
    }, [customerId]);

    useEffect(() => {
        fetchCustomer();
        fetchIntegrations();
    }, [customerId, fetchCustomer, fetchIntegrations]);
    
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleString('fr-FR');
    };
    
    const renderTextWithLinks = (text) => {
        if (!text) return null;
        
        // Regex pour détecter les URLs
        const urlRegex = /(https?:\/\/[^\s]+)/g;
        const parts = text.split(urlRegex);
        
        return parts.map((part, index) => {
            if (urlRegex.test(part)) {
                return (
                    <Link
                        key={index}
                        href={part}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                            color: 'primary.main',
                            textDecoration: 'none',
                            '&:hover': {
                                textDecoration: 'underline'
                            }
                        }}
                    >
                        {part}
                    </Link>
                );
            }
            return part;
        });
    };
    
    return (
        <Layout>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Breadcrumbs aria-label="breadcrumb">
                    <Link component={RouterLink} to="/customers" underline="hover">{t('Customer management')}</Link>
                    <Typography>{customer?.name}</Typography>
                </Breadcrumbs>
                <Link component={RouterLink} to="/customers" underline="hover" sx={{ display: 'flex', alignItems: 'center' }}>
                    <ArrowBackIcon sx={{ mr: 0.5 }} />
                    {t('Back to list')}
                </Link>
            </Box>
            <CustomerTabs customer={customer} currentTab="integrations" />
            
            <Box>
                
                {!integrationsLoaded ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                    </Box>
                ) : integrations.length === 0 ? (
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Card sx={{ textAlign: 'center', py: 4 }}>
                                <CardContent>
                                    <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                                        {t('No integrations')}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {t('This customer has no integrations configured yet.')}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                ) : (
                    <Grid container spacing={3}>
                        {integrations.map((integration) => (
                            <Grid item xs={12} key={integration.id}>
                                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                    <CardContent sx={{ flexGrow: 1, mb: -3 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                            <Chip 
                                                label={integration.key} 
                                                color="primary" 
                                                size="small"
                                                sx={{ fontWeight: 'bold' }}
                                            />
                                        </Box>
                                        
                                        {integration.description && (
                                            <Typography variant="body2" sx={{ mb: 2 }}>
                                                {renderTextWithLinks(integration.description)}
                                            </Typography>
                                        )}
                                        
                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                {t('Created at')}
                                            </Typography>
                                            <Typography variant="body2">
                                                {formatDate(integration.created_at)}
                                            </Typography>
                                        </Box>
                                        
                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                {t('Updated at')}
                                            </Typography>
                                            <Typography variant="body2">
                                                {formatDate(integration.updated_at)}
                                            </Typography>
                                        </Box>
                                        
                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                JSON URL
                                            </Typography>
                                            <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                                                {t('Actions')}{' : '}
                                                <Link href={integration.actions_url} target="_blank" rel="noopener noreferrer" sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                                                    {integration.actions_url}
                                                </Link>
                                            </Typography>
                                            <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                                                {t('Enums')}{' : '}
                                            {integration.enums_url ? (
                                                <Link href={integration.enums_url} target="_blank" rel="noopener noreferrer" sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                                                    {integration.enums_url}
                                                </Link>
                                            ) : (
                                                t('Undefined')
                                            )}
                                            </Typography>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Box>
        </Layout>
    );
}

export default CustomerIntegrationsPage;
